import { type RefObject, useEffect, useRef } from 'react';

const ZOOM_FACTOR = 3;

export const useZoom = (carousel: RefObject<HTMLDivElement | null> | null) => {
  const zoomFactor = useRef(1);
  const hasMouse = useRef<boolean | undefined>(undefined);

  useEffect(() => {
    hasMouse.current = window?.matchMedia(`(pointer: fine)`).matches;
  }, []);

  if (!carousel) return { zoomHandleClick: undefined };
  const caro = carousel.current;

  const handleMouseMove = (event: MouseEvent) => {
    event.preventDefault();

    const carouselElement = caro?.querySelector('.current');
    const imgContainer = caro?.querySelector('.current > div');

    if (carouselElement && imgContainer) {
      const rect = carouselElement.getBoundingClientRect();
      const offsetX = event.clientX - rect.left;
      const offsetY = event.clientY - rect.top;

      (imgContainer as HTMLElement).style.transformOrigin = `${offsetX}px ${offsetY}px`;
    }
  };

  const handleClick = () => {
    const carouselElement = caro?.querySelector('.current');
    const imgContainer = caro?.querySelector('.current > div');
    if (!imgContainer || !carouselElement) return;

    if (zoomFactor.current > 1) {
      zoomFactor.current = 1;
      caro?.removeEventListener('mousemove', handleMouseMove);
      carouselElement.classList.remove('zoomed');
    } else {
      zoomFactor.current = ZOOM_FACTOR;
      caro?.addEventListener('mousemove', handleMouseMove);
      carouselElement.classList.add('zoomed');
    }

    (imgContainer as HTMLElement).style.transform = `scale(${zoomFactor.current})`;
  };

  const start = { x: 0, y: 0, distance: 0 };
  let imageTouchScale = 1;

  const distance = (event: TouchEvent) => {
    return Math.hypot(
      event.touches[0].pageX - event.touches[1].pageX,
      event.touches[0].pageY - event.touches[1].pageY,
    );
  };

  const handleTouchStart = (event: TouchEvent) => {
    if (event.touches.length === 2) {
      event.preventDefault();
      event.stopPropagation();

      // Calculate where the fingers have started on the X and Y axis
      start.x = (event.touches[0].pageX + event.touches[1].pageX) / 2;
      start.y = (event.touches[0].pageY + event.touches[1].pageY) / 2;
      start.distance = distance(event);
    }
  };

  const handleTouchMove = (event: TouchEvent) => {
    const imgContainer = event.target as HTMLElement;
    if (event.touches.length === 2 && imgContainer) {
      event.preventDefault();
      event.stopPropagation();

      // Safari provides event.scale as two fingers move on the screen
      // For other browsers just calculate the scale manually
      let scale: number;
      if (event.scale) {
        scale = event.scale;
      } else {
        const deltaDistance = distance(event);
        scale = deltaDistance / start.distance;
      }
      imageTouchScale = Math.min(Math.max(1, scale), 4);

      // Calculate how much the fingers have moved on the X and Y axis
      // * 2 for accelerated movement
      const deltaX = ((event.touches[0].pageX + event.touches[1].pageX) / 2 - start.x) * 2;
      const deltaY = ((event.touches[0].pageY + event.touches[1].pageY) / 2 - start.y) * 2;

      const transform = `translate3d(${deltaX}px, ${deltaY}px, 0) scale(${imageTouchScale})`;
      imgContainer.style.transform = transform;
    }
  };

  const handleTouchEnd = (event: TouchEvent) => {
    const imgContainer = event.target as HTMLElement;
    imgContainer.style.transform = '';
  };

  return {
    zoomHandleClick: hasMouse.current ? handleClick : undefined,
    onTouchStart: hasMouse.current ? undefined : handleTouchStart,
    onTouchMove: hasMouse.current ? undefined : handleTouchMove,
    onTouchEnd: hasMouse.current ? undefined : handleTouchEnd,
  };
};
